import { useEffect } from "react";
import { useParams } from "@tanstack/react-router";
import { useForm } from "@subly/s1/features/form/fabric";

import { WelcomeStep } from "core/models/welcome";
import core from "core";
import strings from "core/strings";
import useScript from "core/hooks/useScript";

import { BrandingSchema, CMSSchema, PricingSchema, type BrandingType, type CMSType, type PricingType, type WelcomeActions } from "./models";
import { useStoreWelcome } from "./store";
import { articlePreview, useMutateConfig, useQueryCms, useQueryConfig, useQueryPaywall } from "./data";


export const useWelcome = () => {
	const params = useParams({ from: "/_private/welcome/$partnerId" });
	const config = useQueryConfig(params.partnerId);
	const { step, setStep } = useStoreWelcome();

	const copy = ((step?: WelcomeStep | null) => {
		switch (step) {
			case WelcomeStep.One:
				return ({
					title: "Add branding details",
					subtitle: "Add your publications branding details."
				});
			case WelcomeStep.Two:
				return ({
					title: "Content Management System and Paywall"
				});
			case WelcomeStep.Three:
				return ({
					title: "Pricing"
				});
			case WelcomeStep.Four:
				return ({
					title: "Subly Integration details",
					subtitle: "Use the following keys to integrate Subly into your paywall. Follow the Integration guide for your CMS + Paywall combination."
				});
			case WelcomeStep.Done:
				return ({
					title: "Congratulations!",
					subtitle: "You’re all set"
				});
			default:
				return ({
					title: "Add branding details",
					subtitle: "Add your publications branding details"
				});
		}
	})(step);

	return { ...config, copy, step, setStep };
};

// BRANDING
export const useWelcomeBranding = () => {
	const params = useParams({ from: "/_private/welcome/$partnerId" });
	const config = useQueryConfig(params.partnerId);
	const update = useMutateConfig();

	const form = useForm({
		defaultValues: {
			logo: {
				light: config.data?.branding.logo.light || "",
				dark: config.data?.branding.logo.dark || "",
				mark: config.data?.branding.logo.logoMark || ""
			},
			url: config.data?.branding.url || ""
		} as BrandingType,
		validators: {
			onSubmit: BrandingSchema
		},
		onSubmitMeta: {} as { type?: WelcomeActions },
		onSubmit: async ({ value, meta: { type } }) => {
			const logo = value.logo;
			const light = await core.ensureImage(logo?.light);
			const dark = await core.ensureImage(logo?.dark);
			const logoMark = await core.ensureImage(logo?.mark);

			await update.mutateAsync({
				partnerId: params.partnerId,
				payload: {
					step: type === "back" ? null : WelcomeStep.Two,
					branding: {
						logo: { light, dark, logoMark },
						url: value.url
					}
				}
			});
		}
	});

	const getArticle = (logo?: string | File) => {
		const a = articlePreview;
		a.partner.logo.dark = core.is.file(logo) ? URL.createObjectURL(logo) : (logo || "");
		return a;
	};

	const upload = {
		size: 2
	};

	return { form, getArticle, upload };
};

// SYSTEM
export const useWelcomeCMS = () => {
	const params = useParams({ from: "/_private/welcome/$partnerId" });
	const config = useQueryConfig(params.partnerId);
	const update = useMutateConfig();
	const system = config.data?.system;

	const cmsList = useQueryCms();
	const paywallList = usePaywall(system?.cmsId?.toString());

	const form = useForm({
		defaultValues: {
			cms: cmsList.data?.find(c => c.value === system?.cmsId?.toString())?.value || "",
			paywall: paywallList.data?.find(p => p.value === system?.paywallId?.toString())?.value || ""
		} as CMSType,
		validators: {
			onSubmit: CMSSchema
		},
		onSubmitMeta: {} as { type?: WelcomeActions },
		onSubmit: async ({ value, meta: { type } }) =>
			await update.mutateAsync({
				partnerId: params.partnerId,
				payload: {
					step: type === "back" ? WelcomeStep.One : WelcomeStep.Three,
					system: {
						cmsId: Number(value.cms),
						paywallId: Number(value.paywall)
					}
				}
			})
	});

	return { cms: cmsList, form };
};

export const usePaywall = useQueryPaywall;

// PRICING
function getActions(values: PricingType) {
	const actions = [
		{ text: "Unlock this Article with", tokens: strings.pluralize(Number(values.articlePrice), "Subly Token"), url: "#", btnText: "Unlock", type: "Article" }
	];

	if (values.pass.enabled) {
		const duration = values.pass.duration;
		const text = duration === 1 ? "a Day Pass" : `${duration}-Day Pass`;
		actions.push({ text: `Unlock ${text}`, tokens: strings.pluralize(Number(values.pass.price), "Subly Token"), url: "#", btnText: "Unlock", type: "Pass" });
	}
	return actions;
}

export const useWelcomePricing = () => {
	const params = useParams({ from: "/_private/welcome/$partnerId" });
	const config = useQueryConfig(params.partnerId);
	const update = useMutateConfig();
	const pricing = config.data?.pricing;

	// For dev env, you need to build runtime and run the preview script
	const scriptUrl = (import.meta.env.MODE === "development" ? "http://localhost:4173" : `${import.meta.env.VITE_HOST + "/client"}`) + "/subly.js";
	const sublyJs = useScript(scriptUrl,
		{
			"id": "sub-script",
			"sub-dev": "true",
			"sub-preview": "true"
		}
	);

	const form = useForm({
		defaultValues: {
			articlePrice: pricing?.tokens || 1,
			pass: {
				enabled: Boolean(pricing?.pass) || false,
				price: pricing?.pass?.tokens || 4,
				duration: pricing?.pass?.duration || 1
			}
		} as PricingType,
		listeners: {
			onMount: ({ formApi }) => {
				window.subly?.preview(getActions(formApi.state.values));
			},
			onChange: ({ formApi }) => {
				window.subly?.preview(getActions(formApi.state.values));
			}
		},
		validators: {
			onChange: PricingSchema
		},
		onSubmitMeta: {} as { type?: WelcomeActions },
		onSubmit: async ({ value, meta: { type } }) =>
			await update.mutateAsync({
				partnerId: params.partnerId,
				payload: {
					step: type === "back" ? WelcomeStep.Two : WelcomeStep.Four,
					pricing: {
						tokens: value.articlePrice,
						pass: value.pass.enabled
							? { tokens: value.pass.price, duration: value.pass.duration }
							: null
					}
				}
			})
	});

	useEffect(() => {
		if (sublyJs !== "loading") {
			form.mount();
			return () => window.subly?.destroy();
		}
	}, [sublyJs]);

	return { form };
};

// INTEGRATION
export const useWelcomeIntegration = () => {
	const params = useParams({ from: "/_private/welcome/$partnerId" });
	const config = useQueryConfig(params.partnerId);
	const update = useMutateConfig();
	const integration = config.data?.integration;

	const form = useForm({
		defaultValues: {
			publicKey: integration?.key || "",
			privateKey: integration?.secret || ""
		},
		onSubmitMeta: {} as { type?: WelcomeActions },
		onSubmit: async ({ meta: { type } }) =>
			await update.mutateAsync({
				partnerId: params.partnerId,
				payload: {
					step: type === "back" ? WelcomeStep.Three : WelcomeStep.Done
				}
			})
	});

	return { form };
};